import React, { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress as Bar } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, Lock, Download, UtensilsCrossed, NotebookPen, Star, Ticket, Crown, Award, Gift, Flame, Trophy } from 'lucide-react';
import { rewardsAPI, giftsAPI, mediaUrl } from '../services/api';
import { ErrorState } from '../components/ErrorState';

const ICONS = { UtensilsCrossed, NotebookPen, Star, Ticket, Crown, Award, Gift };
const RIcon = ({ name, className }) => { const I = ICONS[name] || Gift; return <I className={className} />; };

const Certificate = React.forwardRef(({ cert }, ref) => (
  <div ref={ref} className="bfc-dark text-white rounded-2xl p-8 text-center relative overflow-hidden" style={{ width: '100%' }}>
    <div className="border-2 border-red-500/40 rounded-xl p-6">
      <img src="/bfc21-logo.png" alt="BFC21" className="w-16 h-16 rounded-full mx-auto mb-3" />
      <p className="text-xs uppercase tracking-[0.3em] text-red-400">Certificat officiel</p>
      <h2 className="text-2xl font-extrabold mt-2">FINISHER BFC21</h2>
      <p className="text-neutral-300 mt-4 text-sm">Décerné à</p>
      <p className="text-xl font-bold text-red-400 mt-1">{cert.name}</p>
      <p className="text-neutral-300 mt-4 text-sm leading-relaxed">
        Pour avoir accompli le Body Fit Challenge 21<br />
        avec <span className="font-bold text-white">{cert.validated_count}/21</span> défis validés
        et <span className="font-bold text-white">{cert.badges_count}</span> badges obtenus.
      </p>
      <p className="text-xs text-neutral-500 mt-5">Délivré le {cert.date} · bodyfitchallenge21</p>
    </div>
  </div>
));

export default function Rewards() {
  const [data, setData] = useState(null);
  const [gifts, setGifts] = useState(null);
  const [error, setError] = useState(false);
  const [openContent, setOpenContent] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const certRef = useRef(null);

  const load = () => {
    setError(false);
    rewardsAPI.list().then(setData).catch(() => setError(true));
    giftsAPI.list().then(setGifts).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (error) return <div className="animate-rise"><ErrorState onRetry={load} /></div>;
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;

  const { points, finished, rewards, next_reward, certificate } = data;
  const pct = next_reward ? Math.min(100, Math.round((points / next_reward.required_points) * 100)) : 100;

  const openReward = (r) => {
    if (!r.unlocked) return;
    if (r.type === 'content') setOpenContent(r);
  };

  const downloadCert = async () => {
    if (!certRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certRef.current, { backgroundColor: '#0B0B0C', scale: 2 });
      const link = document.createElement('a');
      link.download = 'certificat-bfc21.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally { setDownloading(false); }
  };

  return (
    <div className="space-y-6 animate-rise">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-red-950 dark:text-red-50">Récompenses</h1>
        <p className="text-muted-foreground text-sm mt-1">Chaque effort compte. Débloque tes récompenses 🎁</p>
      </div>

      {/* Points + next */}
      <Card className="p-5 rounded-2xl bfc-gradient text-white border-0">
        <div className="flex items-center gap-2"><Flame className="w-5 h-5" /><span className="text-sm font-semibold">Tu as {points} points</span></div>
        {next_reward ? (
          <>
            <div className="flex items-center justify-between mt-3 mb-1 text-sm">
              <span className="opacity-90">Prochaine : {next_reward.name}</span>
              <span className="font-bold">{points}/{next_reward.required_points}</span>
            </div>
            <Bar value={pct} className="h-2 bg-white/30" />
          </>
        ) : <p className="text-sm mt-2 opacity-90">Tu as débloqué toutes les récompenses de points !</p>}
      </Card>

      {/* Gifts to win (Top 10) */}
      {gifts && gifts.gifts && gifts.gifts.length > 0 && (
        <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="gifts-catalog">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-red-950 dark:text-red-50 flex items-center gap-2"><Trophy className="w-5 h-5 text-orange-500" />Cadeaux à gagner</h3>
            {gifts.total_members >= 10 && (
              <span className="text-xs font-semibold text-muted-foreground">{gifts.in_top10 ? `Tu es Top 10 (${gifts.rank}e) 🎉` : `${gifts.rank}e`}</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-4">Réservés au Top 10 du classement. L'attribution finale est validée par le coach.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {gifts.gifts.map((g) => {
              const unlocked = g.status === 'eligible' || g.status === 'awarded' || g.status === 'reached';
              const statusLabel = g.status === 'awarded' ? 'Attribué ✅' : g.status === 'eligible' ? 'Éligible 🎉' : g.status === 'reached' ? 'Palier atteint' : `${g.required_points} points requis`;
              const statusColor = g.status === 'awarded' ? 'text-green-600' : g.status === 'eligible' ? 'text-orange-600' : g.status === 'reached' ? 'text-red-600' : 'text-muted-foreground';
              return (
                <div key={g.gift_id} data-testid={`gift-card-${g.gift_id}`} className={`p-4 rounded-2xl border ${unlocked ? 'border-red-200 dark:border-red-800 bg-white dark:bg-red-950/30' : 'border-border bg-muted/40 opacity-80'}`}>
                  <div className="flex gap-3">
                    <div className={`w-14 h-14 rounded-xl overflow-hidden flex items-center justify-center shrink-0 ${unlocked ? 'bfc-gradient-warm text-white' : 'bg-muted text-muted-foreground'}`}>
                      {g.photo_url ? <img src={mediaUrl(g.photo_url)} alt={g.name} className="w-full h-full object-cover" /> : unlocked ? <Gift className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-red-950 dark:text-red-50">{g.name}</p>
                      {g.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{g.description}</p>}
                      <p className={`text-[11px] font-semibold mt-1 ${statusColor}`}>{statusLabel}</p>
                      {g.conditions && <p className="text-[10px] text-muted-foreground mt-0.5">{g.conditions}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Certificate */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="certificate-section">
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-3 flex items-center gap-2"><Award className="w-5 h-5 text-red-600" />Certificat Finisher</h3>
        {finished ? (
          <>
            <Certificate ref={certRef} cert={certificate} />
            <Button onClick={downloadCert} disabled={downloading} data-testid="download-certificate" className="w-full mt-3 bg-orange-500 hover:bg-orange-600 rounded-xl font-bold">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}Télécharger mon certificat
            </Button>
          </>
        ) : (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-muted">
            <Lock className="w-6 h-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Termine les 21 jours ({certificate.validated_count}/21) pour débloquer ton certificat officiel.</p>
          </div>
        )}
      </Card>

      {/* Rewards grid */}
      <div className="grid sm:grid-cols-2 gap-3">
        {rewards.filter((r) => r.reward_id !== 'certificat').map((r) => (
          <button
            key={r.reward_id}
            onClick={() => openReward(r)}
            data-testid={`reward-${r.reward_id}`}
            className={`text-left p-4 rounded-2xl border transition-all ${r.unlocked ? 'border-red-200 dark:border-red-800 bg-white dark:bg-red-950/30 hover:shadow-md' : 'border-border bg-muted/50 opacity-70'}`}
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-2 ${r.unlocked ? 'bfc-gradient-warm text-white' : 'bg-muted text-muted-foreground'}`}>
              {r.unlocked ? <RIcon name={r.icon} className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
            </div>
            <p className="font-bold text-sm text-red-950 dark:text-red-50">{r.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>
            {!r.unlocked && !r.requires_finish && <p className="text-[11px] font-semibold text-red-600 mt-1">{r.required_points} points requis</p>}
            {!r.unlocked && r.requires_finish && <p className="text-[11px] font-semibold text-red-600 mt-1">Réservé aux finishers</p>}
          </button>
        ))}
      </div>

      <Dialog open={!!openContent} onOpenChange={() => setOpenContent(null)}>
        <DialogContent className="max-w-md" data-testid="reward-content-dialog">
          <DialogHeader><DialogTitle>{openContent?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{openContent?.content}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
